<script lang="ts">
  import * as _ from "lodash";
  import { createEventDispatcher } from "svelte";
  import { params } from "@roxi/routify";

  // components
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import FiltersModal from "@retailer/svelte/Shared/QuickOrderList/FiltersModal.svelte";
  import FulfillmentMethodBanner from "@retailer/svelte/Shared/FulfillmentMethodBanner.svelte";
  import Search from "@retailer/svelte/Shared/QuickOrderList/Search.svelte";
  import SortSelect from "@shared/svelte/Components/SortSelect.svelte";
  import { getControlStateAuthenticationService } from "@retailer/services/retailer_user_authentications_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductListEntity } from "@shared/services/event_tracking/entities";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { hideFulfillmentMethodPickerForDistributor } from "@retailer/services/fulfillment_methods_service";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  // stores
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import { smartFilter } from "@retailer/svelte/stores/QuickOrderListSmartFilterStore";

  // types
  import type {
    QuickOrderListDetail,
    QuickOrderListFilters,
    QuickOrderListParams,
  } from "@shared/types/QuickOrderList";
  import type { Distributor } from "@shared/types/Distributor";
  import type { Product } from "@shared/types/ProductLine";

  export let currentPage: string;
  export let filters: QuickOrderListParams;
  export let filterValues: QuickOrderListFilters;
  export let forSculptureRetailer: boolean = false;
  export let limitedOrdering: boolean;
  export let list: QuickOrderListDetail;
  export let products: Product[] = [];
  export let showInventory: boolean = false;
  export let totalProducts: number = 0;
  export let useListLayout: boolean;
  export let visibleProducts: number = 0;

  let authService = getControlStateAuthenticationService();

  const { trackClick } = EventTracking;

  const trackingParams = [
    new ProductListEntity({
      quick_order_list_id: list.id,
      name: list.name,
      layout: useListLayout ? "list" : "grid",
    }),
  ];

  const hasFulfillmentMethodsRollout = stagedRolloutCheck("marketplace__fulfillment_methods");

  let filterNum: number = 0;
  let hiddenFilters = [];
  let selectedSortOption: any = {};
  let showFiltersModal: boolean = false;

  let nonFilterParams: string[] = [
    "layout",
    "page",
    "sort",
    "direction",
    "search",
    "listId",
    "show_all_products",
    "smart_filter_distributor",
    "deleted",
    "editMode",
  ];

  let sortOptions = [
    {
      text: "Product A - Z",
      sort: "product_line_name",
      direction: "ASC",
    },
    {
      text: "Product Z - A",
      sort: "product_line_name",
      direction: "DESC",
    },
    {
      text: "Type A - Z",
      sort: "product_type_name",
      direction: "ASC",
    },
    {
      text: "Type Z - A",
      sort: "product_type_name",
      direction: "DESC",
    },
    {
      text: "Last ordered (recent)",
      sort: "last_ordered_at",
      direction: "DESC",
    },
    {
      text: "Last ordered (oldest)",
      sort: "last_ordered_at",
      direction: "ASC",
    },
  ];

  $: selectedOptionValue = selectedSortOption.text;

  const applyFilters = function (filters) {
    showFiltersModal = false;
    filters.page = 1;
    dispatch("selectFilter", { filters });
  };

  const closeMobileFilters = function () {
    showFiltersModal = false;
  };

  const computeSelectedSortOption = () => {
    return (
      sortOptions.find(
        op =>
          `${filters.sort}:${filters.direction}`.toLowerCase() ===
          `${op.sort}:${op.direction}`.toLowerCase(),
      ) || sortOptions[0]
    );
  };

  const dispatch = createEventDispatcher();

  const filter = function () {
    filters.page = 1;
    if (!selectedSortOption) {
      selectedSortOption = {};
    }
    filters.sort = selectedSortOption.sort;
    filters.direction = selectedSortOption.direction;
    setLoading();
    dispatch("selectFilter", { filters });
  };

  const openMobileFilters = function () {
    showFiltersModal = true;
  };

  const redoSort = function (e) {
    let sortName = e.target.value;
    let sortOption = _.find(sortOptions, function (x) {
      return x.text === sortName;
    });
    if (sortOption) {
      selectedSortOption = sortOption;
      trackClick(`list_sorted_${selectedSortOption.sort}`, trackingParams);
      filter();
    }
  };

  const setLoading = () => {
    list.loading = true;
  };

  const setupFilters = function () {
    Object.entries(filters).forEach(param => {
      if (param[0] != "distributor" && Number.isInteger(parseInt(param[1]))) {
        filters[param[0]] = parseInt(param[1]);
      }
    });
    if (!filters.distributor) filters.distributor = null;
    if (!filters.container) filters.container = null;
    if (!filters.category) filters.category = null;
    if (!filters.product_type) filters.product_type = null;
  };

  const toggleLayout = (isList: boolean) => {
    filters["layout"] = isList ? "list" : "grid";
    trackClick(`toggle_lists_to_${filters["layout"]}_view`);
    localStorage.setItem("listLayout", filters["layout"]);

    return (useListLayout = isList);
  };

  if (forSculptureRetailer) {
    sortOptions = [
      {
        text: "Default Order",
        sort: "position",
        direction: "ASC",
      },
      {
        text: "Sculpture Suggested",
        sort: "sculpture_suggested",
        direction: "DESC",
      },
      {
        text: "Product A - Z",
        sort: "product_line_name",
        direction: "ASC",
      },
      {
        text: "Product Z - A",
        sort: "product_line_name",
        direction: "DESC",
      },
    ];
  }

  $: {
    if (
      !$smartFilter.distributor &&
      !_.find(sortOptions, function (x) {
        return x.sort === "distributor_name";
      })
    ) {
      sortOptions.push(
        {
          text: "Distributor A - Z",
          sort: "distributor_name",
          direction: "ASC",
        },
        {
          text: "Distributor Z - A",
          sort: "distributor_name",
          direction: "DESC",
        },
      );
    } else if (
      $smartFilter.distributor &&
      _.find(sortOptions, function (x) {
        return x.sort === "distributor_name";
      })
    ) {
      sortOptions = _.filter(sortOptions, function (x) {
        return x.sort !== "distributor_name";
      });
    } else if (
      !forSculptureRetailer &&
      !_.find(sortOptions, option => option.sort === "position")
    ) {
      sortOptions.push({
        text: "Custom Order",
        sort: "position",
        direction: "ASC",
      });
      sortOptions = sortOptions;
    }
  }

  $: {
    filters;
    selectedSortOption = computeSelectedSortOption();
  }

  $: {
    filters.listId = $params.listId;
    if ($smartFilter.distributor) {
      hiddenFilters.push("distributor");
    }
    setupFilters();
    if (filters) {
      filterNum = Object.entries(filters).filter(filter => {
        return (
          !nonFilterParams.includes(filter[0]) && !hiddenFilters.includes(filter[0]) && filter[1]
        );
      }).length;
    }
  }

  $: warehousePickupDistributors = filterValues.distributors?.filter(
    distributor => distributor.uses_warehouse_pickup,
  );

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );

  const needsAccountNumber = async (distributor: Distributor): Promise<boolean> => {
    if (!wineCellarStagedRolloutEnabled)
      return (
        distributor?.requires_account_number &&
        !distributor?.is_rolling_out_integrated_orders &&
        !distributor?.account_number
      );

    const sku = products[0]?.inventory?.sku;
    return await isAccountNumberRequired(distributor?.id, sku, null, distributor?.account_number);
  };
</script>

<style>
  .priority-hide {
    display: none !important;
  }
</style>

{#if currentPage == "layout.list_details" && limitedOrdering}
  <div class="fizz-alert-info fizz-stack-24" data-testid="approved-order-message">
    <p><strong>Approved order list</strong></p>
    <p>
      This order guide was created in partnership with your corporate beverage team. No changes can
      be made to it.
    </p>
  </div>
{/if}
<div class="fizz-stack-16">
  <div class="fizz-flex-row-4 fizz-flex-wrap" style="--gap: 0.25rem;">
    <Search bind:filters on:selectFilter {setLoading} />
    <select
      bind:value={filters.distributor}
      on:change={filter}
      on:click={() => trackClick("list_distributor_filter", trackingParams)}
      class:priority-hide={$smartFilter.distributor}
      class="fizz-select fizz-hide-on-small desktop-filters"
      data-testid="distributor-select"
      id={"distributor_filter"}
      name={"distributor"}
      style="width: 160px"
    >
      {#if !$smartFilter.distributor}
        <option value={null}>All Distributors</option>
      {/if}
      {#each _.sortBy(filterValues.distributors, distributor => distributor.name) as distributor}
        {#if !authService?.shouldHideAllDistributorInfo?.( $RetailerUserAuthenticationsStore, [distributor.id], )}
          <option data-testid="distributor-option" value={distributor.name}>
            {distributor.name}
          </option>
        {/if}
      {/each}
    </select>
    <select
      bind:value={filters.product_type}
      on:change={filter}
      on:click={() => trackClick("list_product_type_filter", trackingParams)}
      class="fizz-select fizz-hide-on-small"
      data-testid="product_type-select"
      id={"product_type_filter"}
      name={"product_type"}
      style="width: 130px"
    >
      <option value={null}>All Types</option>
      {#each _.sortBy(filterValues.productTypes, product => product.name) as type}
        <option data-testid="product_type-option" value={type.id}>{type.name}</option>
      {/each}
    </select>
    <select
      bind:value={filters.category}
      on:change={filter}
      on:click={() => trackClick("list_category_filter", trackingParams)}
      class="fizz-select fizz-hide-on-small"
      data-testid="category-select"
      id={"category_filter"}
      name={"category"}
      style="width: 150px"
    >
      <option value={null}>All Categories</option>
      {#each _.sortBy(filterValues.categories, category => category.name) as category}
        <option data-testid="category-option" value={category.id}>{category.name}</option>
      {/each}
    </select>
    <select
      bind:value={filters.container}
      on:change={filter}
      on:click={() => trackClick("list_container_filter", trackingParams)}
      class="fizz-select fizz-hide-on-small capitalize"
      data-testid="container-select"
      id={"container_filter"}
      name={"container"}
    >
      <option value={null}>All Containers</option>
      {#each _.sortBy(filterValues.containers) as container}
        <option data-testid="container-option" value={container}>{container}</option>
      {/each}
    </select>
  </div>
  <div class="fizz-flex-row-8">
    <select
      on:change={redoSort}
      class="fizz-button fizz-select fizz-text-brand fizz-full-width fizz-flex-item fizz-hide-on-large"
      data-testid="sort-select"
      id="sort-picker"
      name="sort"
      value={selectedSortOption.text}
    >
      {#each sortOptions as sort}
        <option data-testid="sort-option" value={sort.text} data-sort-name={sort.sort}>
          Sort: {sort.text}
        </option>
      {/each}
    </select>
    <button
      class="fizz-button fizz-full-width fizz-flex-item fizz-inset-horizontal-8 fizz-hide-on-large"
      on:click|preventDefault={openMobileFilters}
    >
      Filter
      {#if filterNum}
        ({filterNum})
      {/if}
    </button>
  </div>
</div>
<div class="fizz-space-between fizz-stack-24">
  {#if list.loading}
    <span class="fizz-text-medium loading">Loading...</span>
  {:else}
    <span class="fizz-text-medium">Showing {visibleProducts} of {totalProducts} products</span>
  {/if}
  <div class="fizz-flex-row-0">
    {#if !showInventory}
      <div class="fizz-tooltip-container" data-testId="views-container">
        <button
          on:click|preventDefault={() => toggleLayout(true)}
          aria-describedby="tooltip-grid"
          class="fizz-icon-button-small fizz-tooltip-trigger"
          id="layout-list"
        >
          <Icon
            extraStyles={useListLayout ? "fill: black;" : "fill: grey;"}
            height="16"
            type="LayoutList"
            width="16"
          />
        </button>
        <div
          class="fizz-tooltip fizz-tooltip-top fizz-tooltip-center"
          id="tooltip-grid"
          role="tooltip"
        >
          List
        </div>
      </div>
      <div class="fizz-tooltip-container {!$LayoutStore.isMobile ? 'fizz-inline-12' : ''}">
        <button
          on:click|preventDefault={() => toggleLayout(false)}
          aria-describedby="tooltip-grid"
          class="fizz-icon-button-small fizz-tooltip-trigger"
          id="layout-grid"
        >
          <Icon
            extraStyles={useListLayout ? "fill: grey;" : "fill: black;"}
            height="16"
            type="LayoutGrid"
            width="16"
          />
        </button>
        <div
          class="fizz-tooltip fizz-tooltip-top fizz-tooltip-center"
          id="tooltip-grid"
          role="tooltip"
        >
          Grid
        </div>
      </div>
    {/if}
    {#if !$LayoutStore.isMobile}
      <SortSelect
        options={sortOptions}
        bind:selectedOptionValue
        onChange={redoSort}
        optionField={"text"}
      />
    {/if}
  </div>
</div>
{#if hasFulfillmentMethodsRollout}
  {#each warehousePickupDistributors as distributor (distributor.id)}
    {#if distributor.uses_wine_cellar && !hideFulfillmentMethodPickerForDistributor(distributor.id)}
      {#await needsAccountNumber(distributor) then needsAccountNumber}
        {#if !needsAccountNumber}
          <div class="fizz-stack-24">
            <FulfillmentMethodBanner {distributor} {products} on:openDrawer />
          </div>
        {/if}
      {/await}
    {/if}
  {/each}
{/if}

<FiltersModal
  bind:hiddenFilters
  bind:showFiltersModal
  {applyFilters}
  {filters}
  {filterValues}
  onCloseMobileFilters={closeMobileFilters}
/>
